import { TAG_TYPES, FormatResponse } from "types";
import { baseApi } from "./baseApi";
import { getQueryParams, QueryParams } from "types/queryParams.types";
import { ContractRecord, CreateContractPayload } from "types/contract.types";

const URL_CONTRACTS = "contracts";

const contractApiService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get list
    getAllContracts: builder.query<
      FormatResponse<{ count: number; rows: ContractRecord[] }>,
      { query: QueryParams }
    >({
      query: (params) => {
        const queryParams = params?.query || params;
        const queryString = getQueryParams(queryParams);
        return {
          url: queryString ? `${URL_CONTRACTS}?${queryString}` : URL_CONTRACTS,
          method: "GET",
        };
      },
      providesTags: [{ type: TAG_TYPES.CONTRACT, id: "LIST" }],
    }),

    // Get by ID
    getContractById: builder.query<
      FormatResponse<ContractRecord>,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `${URL_CONTRACTS}/${id}`,
        method: "GET",
      }),
      providesTags: (_result, _error, { id }) => [
        { type: TAG_TYPES.CONTRACT, id },
      ],
    }),

    // Create new contract
    createContract: builder.mutation<
      FormatResponse<ContractRecord>,
      CreateContractPayload
    >({
      query: (data) => ({
        url: URL_CONTRACTS,
        method: "POST",
        data,
      }),
      invalidatesTags: [{ type: TAG_TYPES.CONTRACT, id: "LIST" }],
    }),

    // Update contract
    updateContract: builder.mutation<
      { statusCode: number; success: boolean; data: ContractRecord },
      { id: string } & Partial<ContractRecord>
    >({
      query: ({ id, ...data }) => ({
        url: `${URL_CONTRACTS}/${id}`,
        method: "PATCH",
        data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: TAG_TYPES.CONTRACT, id },
        { type: TAG_TYPES.CONTRACT, id: "LIST" },
      ],
    }),

    // Delete contract
    deleteContract: builder.mutation<
      FormatResponse<{ success: boolean }>,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `${URL_CONTRACTS}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: TAG_TYPES.CONTRACT, id },
        { type: TAG_TYPES.CONTRACT, id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetAllContractsQuery,
  useGetContractByIdQuery,
  useCreateContractMutation,
  useUpdateContractMutation,
  useDeleteContractMutation,
} = contractApiService;
